import { useEffect, useState } from "react";
import { Facebook, Link2, MessageCircle, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { flyerUrl } from "../lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const enc = encodeURIComponent;

const XIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1l8.03-9.18L.6 2h7.07l4.88 6.45L18.244 2zm-1.21 18h1.9L6.98 3.9H4.94L17.03 20z" /></svg>
);

const shareText = (listing, t, lang) => {
  const lost = listing.lf_type === "lost";
  const spot = (lang === "fr" && listing.lf_last_seen_fr) || listing.lf_last_seen || listing.city || "";
  return (lost ? t("fsh_lost") : t("fsh_found")).replace("{title}", listing.title).replace("{spot}", spot);
};

export const FlyerShare = ({ listing, lang }) => {
  const { t } = useLanguage();
  const [file, setFile] = useState(null);
  const url = `${BASE}/share/flyer/${listing.id}?lang=${lang}`;
  const text = shareText(listing, t, lang);
  const canNative = typeof navigator !== "undefined" && !!navigator.share;
  const src = flyerUrl(listing, { lang, w: 1080, v: listing.flyer_phone || "" });

  useEffect(() => {
    if (!canNative) return undefined;
    let live = true;
    setFile(null);
    fetch(src).then((r) => r.blob())
      .then((b) => { if (live) setFile(new File([b], `${listing.lf_type}-pet-flyer.jpg`, { type: "image/jpeg" })); })
      .catch(() => {});
    return () => { live = false; };
  }, [src, canNative, listing.lf_type]);

  const nativeShare = () => {
    const data = file && navigator.canShare?.({ files: [file] }) ? { files: [file], text: `${text} ${url}` } : { text, url };
    navigator.share(data).catch((e) => { if (e?.name !== "AbortError") toast.error(t("fsh_failed")); });
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success(t("fsh_copied")); }
    catch { toast.error(t("fsh_failed")); }
  };

  const links = [
    { id: "facebook", label: "Facebook", icon: <Facebook size={15} />, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`, cls: "bg-[#1877F2] hover:bg-[#166FE5] text-white" },
    { id: "whatsapp", label: "WhatsApp", icon: <MessageCircle size={15} />, href: `https://wa.me/?text=${enc(`${text} ${url}`)}`, cls: "bg-[#25D366] hover:bg-[#1EBE5A] text-white" },
    { id: "sms", label: t("fsh_sms"), icon: <MessageSquare size={15} />, href: `sms:?&body=${enc(`${text} ${url}`)}`, cls: "bg-[#12234A] hover:bg-[#1c3166] text-white", self: true },
    { id: "x", label: "X", icon: <XIcon />, href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`, cls: "bg-black hover:bg-slate-800 text-white" },
  ];
  const pill = "inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold transition-colors";

  return (
    <div className="flex flex-wrap items-center justify-center gap-2" data-testid="flyer-share">
      <span className="w-full text-center text-xs font-bold uppercase tracking-wider text-slate-400">{t("fsh_title")}</span>
      {canNative && (
        <button type="button" onClick={nativeShare} data-testid="flyer-share-native" data-ready={file ? "1" : "0"}
          className={`${pill} bg-[#F5551E] hover:bg-[#e04812] text-white`}>
          <Share2 size={15} /> {t("fsh_native")}
        </button>
      )}
      {links.map((l) => (
        <a key={l.id} href={l.href} target={l.self ? undefined : "_blank"} rel="noopener noreferrer" data-testid={`flyer-share-${l.id}`}
          className={`${pill} ${l.cls}`}>{l.icon} {l.label}</a>
      ))}
      <button type="button" onClick={copy} data-testid="flyer-share-copy"
        className={`${pill} border border-slate-200 bg-white text-[#12234A] hover:border-[#F5551E]`}><Link2 size={15} /> {t("fsh_copy")}</button>
    </div>
  );
};
